import axios from 'axios';
import { store } from '../redux/store';
import { ApiProblemDetails } from '../types/error';

export interface ISubscriptionRoleService {
  canAcceptInvite(inviteId: string): Promise<void>;
  acceptInvite(inviteId: string): Promise<boolean>;
}

export interface SrsRequestHeaders {
  [key: string]: string;
  Authorization: string;
}

export class InviteAcceptanceError extends Error {
  constructor(
    public statusCode: number,
    public problemDetails?: ApiProblemDetails
  ) {
    super(`Invite acceptance failed with status ${statusCode}`);
    this.name = 'InviteAcceptanceError';
  }
}

class SubscriptionRoleService implements ISubscriptionRoleService {
  private getSrsApiConfig = () => store.getState().apiConfig.srs;

  async canAcceptInvite(inviteId: string): Promise<void> {
    try {
      const accessToken = await this.getAccessToken();
      const config = this.getSrsApiConfig();
      await axios.get<void>(`${config.apiRoot}/invites/${inviteId}/can-accept`, {
        headers: this.getSrsRequestHeaders(accessToken),
      });
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const statusCode = error.response.status;
        const problemDetails = error.response.data as ApiProblemDetails | undefined;
        throw new InviteAcceptanceError(statusCode, problemDetails);
      }
      throw error;
    }
  }

  async acceptInvite(inviteId: string): Promise<boolean> {
    try {
      const accessToken = await this.getAccessToken();
      const config = this.getSrsApiConfig();
      const response = await axios.post<void>(
        `${config.apiRoot}/invites/${inviteId}/accept`,
        undefined,
        {
          headers: this.getSrsRequestHeaders(accessToken),
        }
      );
      return response.status >= 200 && response.status < 300;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const statusCode = error.response.status;
        const problemDetails = error.response.data as ApiProblemDetails | undefined;
        throw new InviteAcceptanceError(statusCode, problemDetails);
      }
      throw error;
    }
  }

  private getSrsRequestHeaders(accessToken: string): SrsRequestHeaders {
    return { Authorization: `Bearer ${accessToken}` };
  }

  private async getAccessToken(): Promise<string> {
    return window.auth.getAccessToken();
  }
}

export const subscriptionRoleService: ISubscriptionRoleService = new SubscriptionRoleService();
